/**
 * 窗口内提示条队列（任务 18c2b）：`GlobalState.notifyPopup` 切片 + 纯 reducer +
 * 一个管超时的控制器。
 *
 * 为什么是 store 切片而不是组件内 state：投递接线在 store 层（`notifyDelivery`，
 * 与 UI 是否挂载无关），提示条只是它的一个输出口；放组件里就得把接线搬进组件，
 * 停在设置页时就收不到了。
 *
 * 两条刻意口径（都在纯 reducer 里，可单测）：
 * - **同会话替换**：一个会话最多占一条提示条，新消息覆盖旧的并重排到最前。
 *   与系统通知的折叠 `tag`（18b）同语义，避免一个人连发五条占满整列。
 * - **最多 `POPUP_MAX` 条**，超出挤掉最旧的。叠太多会糊住右下角半屏，而桌面
 *   右下角还可能压着别的浮层。
 *
 * 判定（要不要出、出什么）全在 `~/push/notifyDecision.shouldPopupInApp`，本文件
 * 不碰设置也不碰权限。
 */
import type { NotifyContent } from '~/push/notifyDecision';
import type { GlobalState, GlobalStore } from './global';

/** 右下角同时最多叠几条 */
export const POPUP_MAX = 3;
/** 自动消失时长（旧版 in_app_popup 约 5s，这里留一点余量给读完长预览） */
export const POPUP_TTL_MS = 6000;

export interface NotifyPopupItem {
  /** 自增 id：列表 key 与「关哪一条」的句柄（会话键会被替换，不能当 key） */
  readonly id: number;
  readonly conversationKey: string;
  readonly title: string;
  readonly body: string;
  /** 空串 = 不显示头像（CDN 未就绪 / 会话无头像） */
  readonly icon: string;
  /** 该会话未读数（>1 时提示条右侧显示角标） */
  readonly unread: number;
}

export interface NotifyPopupSlice {
  /** 最新的在前（渲染顺序＝数组顺序，自上而下） */
  readonly items: readonly NotifyPopupItem[];
}

export const NOTIFY_POPUP_INITIAL: NotifyPopupSlice = { items: [] };

/** 提示条内容由 NotifyContent 成形（口径单点在 notifyDecision），这里只取展示要用的字段 */
export function popupItemOf(content: NotifyContent, id: number): NotifyPopupItem {
  return {
    id,
    conversationKey: content.conversationKey,
    title: content.title,
    body: content.body,
    icon: content.icon,
    unread: content.unread,
  };
}

/** 入队一条：同会话替换 + 置顶 + 截到 POPUP_MAX（纯函数） */
export function pushPopup(
  items: readonly NotifyPopupItem[],
  item: NotifyPopupItem,
  max = POPUP_MAX,
): readonly NotifyPopupItem[] {
  const rest = items.filter((it) => it.conversationKey !== item.conversationKey);
  return [item, ...rest].slice(0, max);
}

/** 出队一条（已不在队里 = 原数组原引用返回，不触发无谓重渲染） */
export function dropPopup(
  items: readonly NotifyPopupItem[],
  id: number,
): readonly NotifyPopupItem[] {
  if (!items.some((it) => it.id === id)) return items;
  return items.filter((it) => it.id !== id);
}

export interface NotifyPopupDeps {
  /** 定时器（测试注入假的）；返回取消函数 */
  setTimer(fn: () => void, ms: number): () => void;
  /** 提示音（静音档不调；失败自行吞掉） */
  playSound(): void;
  /** 用户点提示条 → 跳到该会话 */
  openChat(conversationKey: string): void;
  ttlMs?: number;
}

export interface NotifyPopupController {
  /** 投递一条提示条（接线层在 notifyDelivery 里调） */
  show(content: NotifyContent): void;
  /** 关掉一条（用户点 ✕ 或点开会话后） */
  dismiss(id: number): void;
  /** 点提示条：跳会话并关掉它 */
  activate(id: number): void;
  /** 清空（登出/换账号） */
  clear(): void;
  dispose(): void;
}

export function createNotifyPopupController(
  store: GlobalStore,
  deps: NotifyPopupDeps,
): NotifyPopupController {
  const ttl = deps.ttlMs ?? POPUP_TTL_MS;
  const timers = new Map<number, () => void>();
  let nextId = 1;

  function items(): readonly NotifyPopupItem[] {
    return store.get().notifyPopup.items;
  }

  function cancelTimer(id: number): void {
    const cancel = timers.get(id);
    if (cancel) {
      timers.delete(id);
      cancel();
    }
  }

  function dismiss(id: number): void {
    cancelTimer(id);
    const next = dropPopup(items(), id);
    if (next !== items()) store.set({ notifyPopup: { items: next } });
  }

  return {
    show(content) {
      const item = popupItemOf(content, nextId++);
      const before = items();
      const next = pushPopup(before, item);
      // 被同会话替换 / 被挤掉的那些，定时器要跟着撤，否则到点会去关一个不存在的 id
      for (const old of before) {
        if (!next.includes(old)) cancelTimer(old.id);
      }
      store.set({ notifyPopup: { items: next } });
      timers.set(
        item.id,
        deps.setTimer(() => dismiss(item.id), ttl),
      );
      if (!content.silent) {
        try {
          deps.playSound();
        } catch {
          // 自动播放被拦/不支持 WebAudio：提示条照出，只是没声
        }
      }
    },
    dismiss,
    activate(id) {
      const item = items().find((it) => it.id === id);
      dismiss(id);
      if (item) deps.openChat(item.conversationKey);
    },
    clear() {
      for (const id of [...timers.keys()]) cancelTimer(id);
      if (items().length > 0) store.set({ notifyPopup: NOTIFY_POPUP_INITIAL });
    },
    dispose() {
      for (const id of [...timers.keys()]) cancelTimer(id);
    },
  };
}

export function selectNotifyPopups(s: GlobalState): readonly NotifyPopupItem[] {
  return s.notifyPopup.items;
}
